import { defineStore } from 'pinia'
import httpClient from '@/api/httpClient'
import qs from 'qs'
import { useMessagesStore } from '@/stores/messages'
import PaginationStorage from '@/utils/pagination_storage'
import { snakeKeyed } from '@/utils/params_helpers'
import { getDefaultPaginationState } from '@/stores/shared/pagination'
import { useStorySearchStore } from '@/stores/story_search'

const idsMatch = (leftId, rightId) => String(leftId) === String(rightId)
const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key)

const pushState = (queryString) => {
  if (`?${queryString}` == window.location.search) return

  const { history, document, location } = window
  history.pushState(
    {},
    document.title,
    `${location.origin}${location.pathname}?${queryString}`
  )
}

export const useStoriesStore = defineStore('stories', {
  state: () => ({
    ...getDefaultPaginationState(),
    stories: []
  }),

  getters: {
    ids: ({ stories }) => stories.map(story => story.id),

    byId: ({ stories }) => (storyId) => {
      return stories.find(({ id }) => idsMatch(id, storyId))
    },

    containsId: ({ stories }) => (id) => {
      return stories.some(story => idsMatch(story.id, id))
    },
  },

  actions: {
    async fetch({ query, page = undefined, perPage = undefined, newDefaultPerPage = undefined }) {
      let pageStore = new PaginationStorage({ page, perPage, newDefaultPerPage })
      let queryParams = { q: query, ...snakeKeyed(pageStore.queryParams) }

      const queryString = qs.stringify(
        queryParams,
        { arrayFormat: 'brackets', skipNull: true }
      )

      return httpClient
        .get(`/api/internal/stories.json?${queryString}`)
        .then(({ data }) => {
          this.stories = data.stories
          this.page = data.meta.page
          return queryString
        })
        .catch(err => {
          console.error(err)
          useMessagesStore().addError(err)
          throw err
        })
    },

    async search() {
      const storySearchStore = useStorySearchStore()
      storySearchStore.saveToWindowStore()

      return this.fetch({ query: storySearchStore.forQueryParams })
        .then(queryString => { pushState(queryString) })
    },

    async searchPage({ page, perPage, newDefaultPerPage } = {}) {
      const storySearchStore = useStorySearchStore()
      let pageStore = new PaginationStorage({ page, perPage, newDefaultPerPage })
      pageStore.save()

      return this.fetch({
        query: storySearchStore.forQueryParams,
        ...pageStore.queryParams
      }).then(queryString => { pushState(queryString) })
    },

    async reload() {
      return this.searchPage({ page: this.page.current })
    },

    async markPublished({ id }) {
      return httpClient.patch(`/api/internal/stories/${id}/publish`).then(() => {
        useMessagesStore().addNotice('Story published')
      })
    },

    updateStory({ story, preserveLocker = false }) {
      if (!this.containsId(story.id)) return

      this.stories = this.stories.map(stry => {
        if (idsMatch(stry.id, story.id)) {
          const nextStory = Object.assign({}, stry, story)

          // For generic update events we keep the current lock state and let
          // explicit `lock` events control lock icon transitions.
          if (preserveLocker || (!hasOwn(story, 'locker') && stry.locker != null)) {
            nextStory.locker = stry.locker
          }

          return Object.assign(nextStory, { changed: true })
        }
        return stry
      })
    },

    async updateSqueue({ id, squeueId }) {
      const existing = this.stories.find(s => idsMatch(s.id, id))
      return httpClient.patch(`/api/internal/stories/${id}`, {
        lock_version: existing?.lock_version ?? 0,
        story: { squeue_id: squeueId }
      }).then(({ data: { story } }) => {
        this.stories = this.stories.map(stry => {
          if (idsMatch(stry.id, story.id)) {
            return Object.assign({}, stry, story)
          }
          return stry
        })
        useMessagesStore().addNotice('Status changed')
      })
    },

    async clone({ id }) {
      return httpClient.post(`/api/internal/stories/${id}/clone`).then(
        ({ data }) => data
      ).catch((error) => {
        console.error('Clone API error:', error)
        throw error
      })
    },

    async destroy({ id }) {
      return httpClient.delete(`/api/internal/stories/${id}`).then(() => {
        useMessagesStore().addNotice('Story spiked')
      })
    }
  }
})
